import type { PublicSigninAnnouncement } from 'types';
import type { ChatSuggestion } from './chat-types';
import { useEffect, useState } from 'react';
import { trackUmami } from '@/lib/umami/umami';
import { umamiLabel } from '@/lib/umami/umami-events';
import { FIRST_MESSAGE_DELAY } from './chat-pacing';
import { buildNewsChapters } from './news-script';
import { useChat } from './use-chat';

type UseNewsChatParams = {
  announcements: readonly PublicSigninAnnouncement[];
  /** False until the announcements request has settled; see useSigninAnnouncements. */
  isReady: boolean;
  /** False while the panel is full screen and the signup chat owns it. */
  isActive: boolean;
};

const MORE_LABEL = 'What else is new?';
const RESTART_LABEL = 'Start over';

/**
 * The login panel's conversation: the newest announcement plays, then the visitor
 * can ask about it, or ask what else is new and get the one before it. The thread
 * accumulates and is never cleared while the panel is docked, so what was already
 * said stays readable above what is being said now.
 */
export const useNewsChat = ({ announcements, isReady, isActive }: UseNewsChatParams) => {
  const chat = useChat();
  const { say, reply, begin, clear, stop } = chat;
  const [chapterIdx, setChapterIdx] = useState(0);
  const [asked, setAsked] = useState<readonly string[]>([]);

  const chapters = buildNewsChapters(announcements);
  const chapter = chapters[chapterIdx] ?? chapters[0];
  // Which announcement is on screen, for the events: its id, or the evergreen copy when
  // nothing is published.
  const announcementKey = String(announcements[chapterIdx]?.id ?? 'evergreen');

  const play = (idx: number) => {
    setChapterIdx(idx);
    setAsked([]);
    say(chapters[idx]?.messages ?? []);
  };

  // Starts from nothing every time the panel becomes active, which is on mount and
  // again when the signup overlay is closed ("reset and restart the announcements").
  useEffect(() => {
    if (!isActive || !isReady) return;
    clear();
    begin({ delay: FIRST_MESSAGE_DELAY, then: () => play(0) });
    return stop;
    // `announcements` is stable once ready (React Query hands back the same array),
    // and `play` is rebuilt every render, so neither belongs in this list.
  }, [isActive, isReady, announcements]);

  const ask = (question: string) => {
    const match = chapter?.questions.find((item) => item.question === question);
    if (!match) return;
    trackUmami('updates-question-asked', { announcement: announcementKey, question: umamiLabel(question) });
    setAsked((current) => [...current, question]);
    reply({ text: question, then: () => say(match.answer) });
  };

  const showMore = () => {
    const isLast = chapterIdx >= chapters.length - 1;
    trackUmami('updates-more-clicked', { announcement: announcementKey });
    reply({ text: isLast ? RESTART_LABEL : MORE_LABEL, then: () => play(isLast ? 0 : chapterIdx + 1) });
  };

  const questions = (chapter?.questions ?? []).filter((item) => !asked.includes(item.question));
  const suggestions: ChatSuggestion[] = questions.map((item) => ({
    label: item.question,
    onPick: () => ask(item.question),
  }));

  if (chapters.length > 1) {
    const isLast = chapterIdx >= chapters.length - 1;
    suggestions.push({ label: isLast ? RESTART_LABEL : MORE_LABEL, onPick: showMore });
  }

  // Nothing is offered while Pertento is mid-sentence: a pill that answers a
  // question the thread has not finished asking is how the order gets scrambled.
  // Nor before anything has been said: for the frame between the chapter being ready
  // and the first message being scheduled, `busy` is still false, and the pills would
  // show under an empty thread.
  return { chat, announcementKey, suggestions: chat.busy || chat.thread.length === 0 ? [] : suggestions };
};
